import { useRollbar } from '@rollbar/react'
import { BadRequest, InternalError } from 'common/errors'
import { BookingPaidCalendarInterface } from 'common/types/entities/booking-paid-calendar-interface'
import { OldEntityInterface } from 'common/types/entities/OldEntityInterface'
import { TwoStepPaymentFormStepInterface } from 'common/types/entities/two-step-payment-form-interface'
import { buyMainOffer, buyOfferBump } from 'publisher/api/mercadoPagoApi'
import {
  getProcessedMercadoErrorCauses,
  isMercadoError,
  isMercadoErrorCausesArray,
} from 'publisher/context/mercadoPago/utils/initCardForm'
import { useMercadoPago } from 'publisher/context/mercadoPagoContext'
import usePaymentSubmit from 'publisher/hooks/payment/usePaymentSubmit'
import usePage, { selectors as pageSelectors } from 'publisher/hooks/usePage'
import { ThreeDsInfo } from 'publisher/paymentProcessors/mercadoPago/components/ThreeDsModal'
import { usePayment } from 'publisher/store'
import paymentSelectors from 'publisher/store/payment/paymentSelectors'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { PaymentButtonEntityInterface } from 'src/shared/entities/payment-button/payment-button.schema'

export default function useMercadoPagoPaymentHandler(
  entity:
    | OldEntityInterface
    | PaymentButtonEntityInterface
    | BookingPaidCalendarInterface
    | TwoStepPaymentFormStepInterface,
) {
  const rollbar = useRollbar()
  const { t } = useTranslation()
  const { errors, setErrors, isLoading, setLoading, submit } =
    usePaymentSubmit(entity)
  const purchaseProcessId = usePayment(paymentSelectors.getPurchaseProcessId)
  const pageId = usePage(pageSelectors.getPageId)
  const { generatePaymentData, setInitMercadoPago } = useMercadoPago()

  useEffect(() => {
    setInitMercadoPago(true)
  }, [])

  const handleSubmit = async (
    e: React.SyntheticEvent,
    handleThreeDs: (
      threeDsInfo: ThreeDsInfo,
      orderBumpOffers: number[],
      redirect?: string,
    ) => void,
  ) => {
    e.preventDefault()

    setLoading(true)
    setErrors([])

    submit(
      async body => {
        const paymentData = await generatePaymentData()

        if (!paymentData) {
          setErrors([t('core.errors.something_went_wrong')])
          setLoading(false)
          return
        }

        const { data } = await buyMainOffer(pageId, purchaseProcessId, {
          payment_form: {
            ...body,
            token: paymentData.token,
            mercadoPagoPaymentMethod: paymentData.paymentMethodId,
            installments: paymentData.installments,
            deviceId: window.MP_DEVICE_SESSION_ID,
          },
        })

        if (data.threeDsInfo) {
          handleThreeDs(data.threeDsInfo, body.orderBumpOffers, data.redirect)
          return
        }

        if (data.redirect) {
          if (body.orderBumpOffers.length > 0) {
            const bumpPaymentData = await generatePaymentData()
            if (bumpPaymentData) {
              await buyOfferBump(pageId, purchaseProcessId, {
                bump_payment_form: {
                  token: bumpPaymentData.token,
                  mercadoPagoPaymentMethod: bumpPaymentData.paymentMethodId,
                  installments: bumpPaymentData.installments,
                },
              })
            }
          }

          window.location.assign(data.redirect)
          return
        }
      },
      error => {
        if (isMercadoError(error)) {
          setErrors(getProcessedMercadoErrorCauses(error.cause, t))
        } else if (isMercadoErrorCausesArray(error)) {
          setErrors(getProcessedMercadoErrorCauses(error, t))
        } else if (error instanceof InternalError) {
          rollbar.error('Mercado pago internal error', error)
        } else if (error instanceof BadRequest) {
          setErrors(error.response.data.errors.common)
        } else {
          rollbar.error('Mercado pago undefined server error', error as Error)
        }
        setLoading(false)
      },
    )
  }

  return { errors, isLoading, setErrors, handleSubmit }
}
